import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from './LangContext';
import LanguageSwitcher from './components/LanguageSwitcher';

function ContactPage() {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch('/send-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, message: `Teléfono: ${form.phone || 'No indicado'}\n\n${form.message}` }),
      });
      const data = await res.json();
      if (data.ok) {
        setSubmitStatus('success');
        setForm({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-on-background">
      <header className="fixed top-0 z-50 h-20 w-full border-b border-white/10 bg-zinc-950/80 backdrop-blur-md">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-6 md:px-8">
          <Link to={prefix + '/'} className="flex items-center gap-3" aria-label="ElmakinaTech">
            <img src="/elmakinatech-icon.svg" alt="" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-tighter text-white">ElmakinaTech</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to={prefix + '/'} className="rounded bg-secondary-container px-5 py-3 font-label-caps text-label-caps text-on-secondary-fixed transition-all duration-300 hover:brightness-110">
              {t('contact.nav')}
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        <section className="mx-auto max-w-7xl px-6 py-16 md:px-8 md:py-24">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <h1 className="hero-title text-4xl sm:text-5xl text-white mb-4">{t('contact.title')}</h1>
              <p className="text-xl text-zinc-400 mb-2">{t('contact.subtitle')}</p>
              <p className="text-zinc-500 mb-10">{t('contact.description')}</p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <span className="material-symbols-outlined text-cyan-400 mt-1">mail</span>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest font-label-caps">{t('contact.info.email')}</p>
                    <a href="mailto:atencionalcliente@elmakinatech.com" className="text-white hover:text-cyan-400 transition-colors">atencionalcliente@elmakinatech.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="material-symbols-outlined text-cyan-400 mt-1">call</span>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest font-label-caps">{t('contact.info.phone')}</p>
                    <a href="tel:+34614183150" className="text-white hover:text-cyan-400 transition-colors">{t('contact.phone')}</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="material-symbols-outlined text-cyan-400 mt-1">location_on</span>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest font-label-caps">{t('contact.info.location')}</p>
                    <p className="text-white">{t('contact.location')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="material-symbols-outlined text-cyan-400 mt-1">schedule</span>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest font-label-caps">{t('contact.info.response')}</p>
                    <p className="text-white">{t('consulting.contact.response')}</p>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="glass-card space-y-6 rounded-lg p-8 md:p-10">
              <div>
                <label className="mb-2 block font-label-caps text-[10px] text-zinc-400" htmlFor="contact-name">{t('contact.form.name')}</label>
                <input className="w-full border border-white/10 bg-zinc-950 p-4 text-white transition-colors focus:border-cyan-400 focus:ring-0" type="text" name="name" id="contact-name" value={form.name} onChange={handleChange} placeholder={t('contact.form.namePlaceholder')} required />
              </div>
              <div>
                <label className="mb-2 block font-label-caps text-[10px] text-zinc-400" htmlFor="contact-email">{t('contact.form.email')}</label>
                <input className="w-full border border-white/10 bg-zinc-950 p-4 text-white transition-colors focus:border-cyan-400 focus:ring-0" type="email" name="email" id="contact-email" value={form.email} onChange={handleChange} placeholder={t('contact.form.emailPlaceholder')} required />
              </div>
              <div>
                <label className="mb-2 block font-label-caps text-[10px] text-zinc-400" htmlFor="contact-phone">{t('contact.form.phone')}</label>
                <input className="w-full border border-white/10 bg-zinc-950 p-4 text-white transition-colors focus:border-cyan-400 focus:ring-0" type="tel" name="phone" id="contact-phone" value={form.phone} onChange={handleChange} placeholder={t('contact.form.phonePlaceholder')} />
              </div>
              <div>
                <label className="mb-2 block font-label-caps text-[10px] text-zinc-400" htmlFor="contact-message">{t('contact.form.message')}</label>
                <textarea className="w-full border border-white/10 bg-zinc-950 p-4 text-white transition-colors focus:border-cyan-400 focus:ring-0" rows="5" name="message" id="contact-message" value={form.message} onChange={handleChange} placeholder={t('contact.form.messagePlaceholder')} required />
              </div>
              {submitStatus === 'success' && (
                <p className="text-emerald-400 text-sm text-center">{t('contact.form.success')}</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-sm text-center">{t('contact.form.error')}</p>
              )}
              <button className="w-full rounded bg-secondary-container py-5 font-label-caps text-label-caps text-on-secondary-fixed transition-all duration-300 hover:brightness-110 disabled:opacity-50" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('contact.form.sending') : t('contact.form.send')}
              </button>
            </form>
          </div>
        </section>
      </main>

      <footer className="bg-zinc-950 w-full py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <Link to={prefix + '/'} className="flex items-center gap-3">
            <img src="/elmakinatech-icon.svg" alt="" className="h-8 w-auto" />
            <span className="text-lg font-black text-white tracking-tighter">ElmakinaTech</span>
          </Link>
          <div className="flex gap-6">
            <Link className="text-zinc-500 font-label-caps text-xs uppercase tracking-widest hover:text-cyan-400 transition-all" to={prefix + '/privacidad'}>{t('home.footer.privacy')}</Link>
            <Link className="text-zinc-500 font-label-caps text-xs uppercase tracking-widest hover:text-cyan-400 transition-all" to={prefix + '/terminos'}>{t('legal.terms.title')}</Link>
            <Link className="text-zinc-500 font-label-caps text-xs uppercase tracking-widest hover:text-cyan-400 transition-all" to={prefix + '/politica-de-cookies'}>{t('legal.cookies.title')}</Link>
            <Link className="text-zinc-500 font-label-caps text-xs uppercase tracking-widest hover:text-cyan-400 transition-all" to={prefix + '/contacto'}>{t('contact.title')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ContactPage;
